/**
 * Global Search Service — federated search for the Command Palette (#419)
 *
 * Aggregates results from three sources into a single grouped payload:
 *
 *  1. Navigation  — sourced from `config/navConfig.NAV_ITEMS` (single source
 *     of truth also used by the sidebar), fuzzy-matched client-side. No
 *     network call.
 *  2. Commands    — a small static list of executable actions ("Nuevo
 *     proyecto", etc), fuzzy-matched client-side, gated by role.
 *  3. Entities    — server-side `ilike` search via the *existing* domain
 *     services (`listProjects`, `listInventory`, `listTools`, `listClients`,
 *     `listQuotations`, `listTasks`). We deliberately never touch Supabase
 *     directly here: RLS is enforced by those services exactly like every
 *     other screen in the app, so a query never returns rows the signed-in
 *     user isn't allowed to see. Client-side fuzzy search was ruled out for
 *     entities because none of these domains are guaranteed to be loaded in
 *     memory at search time and full tables can be large (see PLAN notes in
 *     issue #419) — pagination/limits + a small debounce keep this cheap.
 *
 * Each entity fetch also fires only when the signed-in role is allowed to
 * see that route at all (reusing `ROUTE_ROLE_REQUIREMENTS`, the same map
 * PrivateLayout/RoleGuard use) — this avoids a pointless request that RLS
 * would zero out anyway and keeps the palette from showing an empty
 * "Clientes" group to a technician.
 *
 * @module services/globalSearchService
 */

import { listProjects } from './projectService';
import { listInventory } from './inventoryService';
import { listClients } from './clientService';
import { listQuotations } from './quotationService';
import { listTasks } from './taskService';
import { listTools } from './toolAssignmentService';
import { NAV_ITEMS, ROUTE_ROLE_REQUIREMENTS } from '../config/navConfig';
import { fuzzyScoreFields, rankItems } from '../utils/fuzzyMatch';

// Fetched per entity group — deliberately a bit larger than what we display
// so "ver más" can reveal extra rows without a second round-trip.
export const GROUP_FETCH_LIMIT = 8;
// Shown per group before the user taps "ver más".
export const GROUP_DISPLAY_LIMIT = 5;

/**
 * Executable commands surfaced alongside plain navigation/entity links
 * (issue #419: "Comandos, no solo enlaces"). `roles: null` = any
 * authenticated private-app role.
 */
export const COMMANDS = [
  {
    id: 'cmd-new-project',
    label: 'Nuevo proyecto',
    icon: 'bi-folder-plus',
    path: '/proyectos/nuevo',
    roles: null,
    keywords: 'crear proyecto nuevo alta',
  },
  {
    id: 'cmd-new-quotation',
    label: 'Nueva cotización',
    icon: 'bi-file-earmark-plus',
    path: '/cotizaciones/nueva',
    roles: null,
    keywords: 'crear cotizacion nueva',
  },
  {
    id: 'cmd-new-task',
    label: 'Nueva tarea',
    icon: 'bi-check2-square',
    path: '/tareas/nuevo',
    roles: null,
    keywords: 'crear tarea nueva pendiente',
  },
  {
    id: 'cmd-new-client',
    label: 'Nuevo cliente',
    icon: 'bi-building-add',
    path: '/clientes/nuevo',
    roles: ['admin'],
    keywords: 'crear cliente nuevo empresa',
  },
  {
    id: 'cmd-new-expense',
    label: 'Nuevo gasto',
    icon: 'bi-cash-stack',
    path: '/gastos/nuevo',
    roles: ['admin'],
    keywords: 'crear gasto nuevo registrar factura',
  },
  {
    id: 'cmd-new-material',
    label: 'Nuevo ítem de inventario',
    icon: 'bi-box-seam',
    path: '/inventario/nuevo',
    // #798: el supervisor da de alta materiales. Mismos roles que el RoleGuard
    // de la ruta en App.js.
    roles: ['admin', 'supervisor'],
    keywords: 'crear inventario material nuevo',
  },
  {
    id: 'cmd-register-tool',
    label: 'Registrar herramienta',
    icon: 'bi-tools',
    // ToolList opens the ToolRegisterModal itself when it sees ?action=register.
    path: '/herramientas?action=register',
    // #798: el supervisor registra herramientas. Mismos roles que el gate de
    // ToolList (`canRegister`).
    roles: ['admin', 'supervisor'],
    keywords: 'nueva herramienta registrar activo',
  },
];

const roleCanAccess = (role, routePrefix) => {
  const allowed = ROUTE_ROLE_REQUIREMENTS[routePrefix];
  if (!allowed) return true; // not gated at the route level — RLS is the real gate
  return allowed.includes(role);
};

/**
 * @typedef {Object} SearchResultItem
 * @property {string} id
 * @property {string} type - group key ('navigation' | 'commands' | 'projects' | ...)
 * @property {string} title
 * @property {string} [subtitle]
 * @property {string} icon - bootstrap-icons class, e.g. 'bi-folder'
 * @property {string} path - react-router destination
 * @property {number} score
 */

/** @returns {SearchResultItem[]} */
const searchNavigation = (query, role) => {
  const items = NAV_ITEMS.filter((item) => item.roles.includes(role));
  const ranked = rankItems(query, items, (item) => [item.label, item.section]);
  return ranked.map(({ item, score }) => ({
    id: `nav-${item.path}`,
    type: 'navigation',
    title: item.label,
    subtitle: item.section || 'Ir a sección',
    icon: item.icon,
    path: item.path,
    score,
  }));
};

/** @returns {SearchResultItem[]} */
const searchCommands = (query, role) => {
  const items = COMMANDS.filter((cmd) => !cmd.roles || cmd.roles.includes(role));
  const ranked = rankItems(query, items, (item) => [item.label, item.keywords]);
  return ranked.map(({ item, score }) => ({
    id: item.id,
    type: 'commands',
    title: item.label,
    subtitle: 'Comando',
    icon: item.icon,
    path: item.path,
    // Commands only surface for a deliberate query — an empty search bar
    // should lead with navigation, not a wall of "Nuevo X" actions.
    score: query ? score + 20 : -1,
  }));
};

/**
 * Wrap a "fetch a small server-searched page" call so a failure in one
 * domain (e.g. a role without RLS access, or a transient network error)
 * never breaks the whole palette.
 *
 * A failed group still degrades to an empty array — one broken domain must
 * not blank the other seven — but the failure is now RECORDED in `failed`
 * instead of being swallowed whole. Before, the palette could not tell
 * "nothing matched" from "the query blew up", so a total outage rendered as
 * the reassuring "Sin resultados para «X»" (#…). Note the two shapes a
 * failure arrives in: the domain services return `{ data, error }` (they
 * resolve, they don't reject), so checking `error` matters as much as the
 * catch block.
 *
 * @param {Function} fn domain list service
 * @param {object} args
 * @param {string} group group key to record on failure
 * @param {string[]} failed mutable collector of failed group keys
 */
const safeList = async (fn, args, group, failed) => {
  try {
    const { data, error } = await fn(args);
    if (error) throw error;
    return data || [];
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[globalSearch] entity fetch failed', err);
    if (failed && group) failed.push(group);
    return [];
  }
};

const searchProjects = async (query, failed) => {
  const rows = await safeList(listProjects, { search: query, pageSize: GROUP_FETCH_LIMIT }, 'projects', failed);
  return rows.map((row) => ({
    id: `project-${row.id}`,
    type: 'projects',
    title: row.title || row.project_number || 'Proyecto sin título',
    subtitle: [row.project_number ? `#${row.project_number}` : null, row.client?.company_name]
      .filter(Boolean)
      .join(' · '),
    icon: 'bi-folder',
    path: `/proyectos/${row.id}`,
    score: 200,
  }));
};

const searchQuotations = async (query, failed) => {
  const rows = await safeList(listQuotations, { search: query, pageSize: GROUP_FETCH_LIMIT }, 'quotations', failed);
  // listQuotations has no text-search filter today — narrow client-side on
  // what we already fetched (small page) so an unfiltered query doesn't
  // dump every quotation into the group.
  const filtered = query
    ? rows.filter((row) => fuzzyScoreFields(query, [row.quotation_number, row.project?.title]) !== null)
    : rows;
  return filtered.slice(0, GROUP_FETCH_LIMIT).map((row) => ({
    id: `quotation-${row.id}`,
    type: 'quotations',
    title: row.project?.title ? `Cotización — ${row.project.title}` : (row.quotation_number || 'Cotización'),
    subtitle: row.quotation_number || row.status,
    icon: 'bi-file-earmark-text',
    path: `/cotizaciones/${row.id}`,
    score: 200,
  }));
};

const searchInventory = async (query, role, failed) => {
  if (!roleCanAccess(role, '/inventario')) return [];
  const rows = await safeList(listInventory, { search: query, pageSize: GROUP_FETCH_LIMIT }, 'inventory', failed);
  return rows.map((row) => ({
    id: `inventory-${row.id}`,
    type: 'inventory',
    title: row.name,
    subtitle: [row.sku, `${row.current_stock ?? 0} ${row.unit || ''}`.trim()].filter(Boolean).join(' · '),
    icon: 'bi-boxes',
    // InventoryList has no single-item detail route today; the edit form is
    // the closest deep link to "this specific item" (documented in PLAN).
    path: `/inventario/${row.id}/editar`,
    score: 200,
  }));
};

const searchTools = async (query, role, failed) => {
  if (!roleCanAccess(role, '/herramientas')) return [];
  const data = await safeList(listTools, { search: query, limit: GROUP_FETCH_LIMIT }, 'tools', failed);
  // #567 — la búsqueda de herramientas ahora degrada a candidatos PARCIALES
  // cuando el AND estricto da cero (ej. "taladro verde" devuelve los 3
  // taladros, con "verde" sin calzar). Eso es útil cuando hay un agente o una
  // pantalla que puede decir "¿cuál de estos?", pero acá NO: la paleta de
  // comandos muestra una lista rasa donde un candidato parcial se lee igual
  // que un acierto. Nos quedamos solo con los exactos.
  const rows = (data || []).filter((row) => row._matchType !== 'partial');
  return rows.map((row) => ({
    id: `tool-${row.id}`,
    type: 'tools',
    title: row.name,
    subtitle: [row._categoryLabel, row._locationLabel].filter(Boolean).join(' · '),
    icon: 'bi-tools',
    path: `/herramientas/${row.id}`,
    score: 200,
  }));
};

const searchClients = async (query, role, failed) => {
  if (!roleCanAccess(role, '/clientes')) return [];
  const rows = await safeList(listClients, { search: query, pageSize: GROUP_FETCH_LIMIT }, 'clients', failed);
  return rows.map((row) => ({
    id: `client-${row.id}`,
    type: 'clients',
    title: row.company_name,
    subtitle: row.primary_contact_name,
    icon: 'bi-building',
    path: `/clientes/${row.id}`,
    score: 200,
  }));
};

const searchTasks = async (query, failed) => {
  const rows = await safeList(listTasks, { search: query, pageSize: GROUP_FETCH_LIMIT, parentTaskId: 'all' }, 'tasks', failed);
  return rows.map((row) => ({
    id: `task-${row.id}`,
    type: 'tasks',
    title: row.title,
    subtitle: [row.task_number ? `#${row.task_number}` : null, row.project?.title].filter(Boolean).join(' · '),
    icon: 'bi-check2-square',
    path: `/tareas/${row.id}`,
    score: 200,
  }));
};

/**
 * Run the full federated search. Entity groups are only requested once the
 * query has at least `MIN_ENTITY_QUERY_LENGTH` characters — Navigation and
 * Commands remain query-free capable (empty query = "browse everything you
 * can do") since they're local/instant, but hammering six tables on every
 * keystroke (including zero/one-char queries) would be wasteful.
 *
 * @param {string} query
 * @param {{ role: string }} options
 * @returns {Promise<Record<string, SearchResultItem[]>>}
 */
export const MIN_ENTITY_QUERY_LENGTH = 2;

export const searchAll = async (query, { role } = {}) => {
  const trimmed = (query || '').trim();

  const navigation = searchNavigation(trimmed, role);
  const commands = searchCommands(trimmed, role);

  if (trimmed.length < MIN_ENTITY_QUERY_LENGTH) {
    return {
      navigation,
      commands,
      projects: [],
      quotations: [],
      inventory: [],
      tools: [],
      clients: [],
      tasks: [],
      errors: [],
    };
  }

  // Collector for domains whose fetch failed. It travels WITH the result
  // (`errors`) rather than as a thrown exception on purpose: a partial
  // outage must still render the groups that worked, and the palette needs
  // to say so out loud instead of calling it "sin resultados".
  const errors = [];

  const [projects, quotations, inventory, tools, clients, tasks] = await Promise.all([
    searchProjects(trimmed, errors),
    searchQuotations(trimmed, errors),
    searchInventory(trimmed, role, errors),
    searchTools(trimmed, role, errors),
    searchClients(trimmed, role, errors),
    searchTasks(trimmed, errors),
  ]);

  return { navigation, commands, projects, quotations, inventory, tools, clients, tasks, errors };
};

/** Group metadata used by the UI to render section headers in a fixed order. */
export const GROUP_LABELS = {
  navigation: 'Navegación',
  commands: 'Comandos',
  projects: 'Proyectos',
  quotations: 'Cotizaciones',
  inventory: 'Inventario',
  tools: 'Herramientas',
  clients: 'Clientes',
  tasks: 'Tareas',
};

export const GROUP_ORDER = ['commands', 'navigation', 'projects', 'quotations', 'tasks', 'inventory', 'tools', 'clients'];

const globalSearchService = { searchAll, COMMANDS, GROUP_LABELS, GROUP_ORDER, GROUP_DISPLAY_LIMIT, GROUP_FETCH_LIMIT, MIN_ENTITY_QUERY_LENGTH };
export default globalSearchService;
